// src/lib/permissions/core/modules.ts
// Centralized permission modules configuration

export type ModuleCategory = 'dashboard' | 'admin' | 'operational' | 'reporting' | 'visualisation' | 'calendar';
export type AccessLevel = 'none' | 'view' | 'update';

export interface PermissionModule {
  name: string;
  displayName: string;
  permissions: string[];
  category: ModuleCategory;
  description?: string;
  dependencies?: string[];
}

/**
 * Complete permission modules configuration
 * Single source of truth for all permission modules
 */
export const PERMISSION_MODULES: PermissionModule[] = [
  // DASHBOARD CATEGORY
  {
    name: 'dashboard',
    displayName: 'Dashboard',
    category: 'dashboard',
    permissions: [
      'dashboard_terminal-tile_view',
      'dashboard_checkpoint-tile_view', 
      'dashboard_queue-tile_view',
      'dashboard_lane-tile_view',
      'dashboard_checkpoint_status_update'
    ],
    description: 'Main dashboard viewing and basic status updates'
  },

  // OPERATIONAL CATEGORY
  {
    name: 'lanes',
    displayName: 'Lanes Management',
    category: 'operational',
    permissions: [
      'lane_view',
      'lane_status_update'
    ],
    description: 'Lane viewing and status management'
  },  {
    name: 'queues',
    displayName: 'Queue Management', 
    category: 'operational',
    permissions: [
      'queue_view',
      'queue_status_update',
      'queue_assignment_update'
    ],
    description: 'Queue viewing and management operations'
  },

  // ADMIN CATEGORY
  {
    name: 'calendar',
    displayName: 'Calendar Administration',
    category: 'calendar',
    permissions: [
      'calendar_view_access',
      'calendar_update_access'
    ],
    description: 'Calendar viewing and editing'
  },
  {
    name: 'admin_terminals',
    displayName: 'Terminal Administration',
    category: 'admin', 
    permissions: [
      'admin_terminal_view_access',
      'admin_terminal_update_access'
    ],
    description: 'Terminal configuration and management'
  },
  {
    name: 'admin_checkpoints',
    displayName: 'Checkpoint Administration',
    category: 'admin',
    permissions: [
      'admin_checkpoint_view_access',
      'admin_checkpoint_update_access'
    ],
    description: 'Checkpoint configuration and management'
  },  {
    name: 'admin_queue_types',
    displayName: 'Queue Type Administration',
    category: 'admin',
    permissions: [
      'admin_queuetype_view_access',
      'admin_queuetype_update_access'
    ],
    description: 'Queue type configuration'
  },
  {
    name: 'admin_queue_assignments',
    displayName: 'Queue Assignment Administration',
    category: 'admin',
    permissions: [
      'admin_queueassignment_view_access',
      'admin_queueassignment_update_access'
    ],
    description: 'Queue assignment management'
  },
  {
    name: 'admin_sections',
    displayName: 'Section Administration',
    category: 'admin',
    permissions: [
      'admin_section_view_access',
      'admin_section_update_access'
    ],
    description: 'Section configuration and management'
  },
  {
    name: 'admin_assets',
    displayName: 'Asset Administration',
    category: 'admin',
    permissions: [
      'admin_asset_view_access',
      'admin_asset_update_access'
    ],
    description: 'Asset management and configuration'
  },  {
    name: 'admin_asset_groups',
    displayName: 'Asset Group Administration',
    category: 'admin',
    permissions: [
      'admin_assetgroup_view_access',
      'admin_assetgroup_update_access'
    ],
    description: 'Asset group management'
  },
  {
    name: 'admin_displays',
    displayName: 'Display Administration',
    category: 'admin',
    permissions: [
      'admin_display_view_access',
      'admin_display_update_access',
      'admin_display_switch'
    ],
    description: 'Display configuration and management including display switch control'
  },
  {
    name: 'power_bi',
    displayName: 'Business Intelligence',
    category: 'reporting',
    permissions: [
      'admin_bi_dashboard_access'
    ],
    description: 'PowerBI dashboard access'
  },
  
  // VISUALISATION CATEGORY
  {
    name: 'visualization_2d',
    displayName: '2D Visualization',
    category: 'visualisation',
    permissions: [
      '2d_visualisation_access'
    ],
    description: '2D data visualization and real-time monitoring'
  },
  {
    name: 'visualization_3d',
    displayName: '3D Visualization', 
    category: 'visualisation',
    permissions: [
      '3d_visualisation_access'
    ],
    description: '3D data visualization with interactive models'
  }
];

/**
 * Helper functions for module management
 */
export const ModuleHelpers = {
  /**
   * Get all modules by category
   */
  getByCategory(category: ModuleCategory): PermissionModule[] {
    return PERMISSION_MODULES.filter(m => m.category === category);
  },
  /**
   * Get module by name
   */
  getByName(name: string): PermissionModule | undefined {
    return PERMISSION_MODULES.find(m => m.name === name);
  },

  /**
   * Get all unique permissions across all modules
   */
  getAllPermissions(): string[] {
    return Array.from(new Set(
      PERMISSION_MODULES.flatMap(m => m.permissions)
    )).sort();
  },

  /**
   * Get permissions for a specific module
   */
  getModulePermissions(moduleName: string): string[] {
    const module = this.getByName(moduleName);
    return module?.permissions || [];
  },

  /**
   * Validate module configuration
   */
  validateConfiguration(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    const allPermissions = new Set<string>();
    const moduleNames = new Set<string>();

    PERMISSION_MODULES.forEach((module, index) => {
      // Check for duplicate module names
      if (moduleNames.has(module.name)) {
        issues.push(`Duplicate module name: ${module.name}`);
      }
      moduleNames.add(module.name);

      // Check for empty permissions
      if (module.permissions.length === 0) {
        issues.push(`Module ${module.name} has no permissions`);
      }
      // Check for duplicate permissions within module
      const modulePermissions = new Set<string>();
      module.permissions.forEach(permission => {
        if (modulePermissions.has(permission)) {
          issues.push(`Duplicate permission in ${module.name}: ${permission}`);
        }
        modulePermissions.add(permission);

        // Check for duplicate permissions across modules
        if (allPermissions.has(permission)) {
          issues.push(`Permission ${permission} exists in multiple modules`);
        }
        allPermissions.add(permission);
      });

      // Check for required fields
      if (!module.name || !module.displayName || !module.category) {
        issues.push(`Module at index ${index} missing required fields`);
      }
    });

    return {
      isValid: issues.length === 0,
      issues
    };
  },

  /**
   * Get statistics about the module configuration
   */
  getStats() {
    const categories = new Set(PERMISSION_MODULES.map(m => m.category));
    const totalPermissions = this.getAllPermissions().length;
    
    const categoryStats = Array.from(categories).map(category => ({
      category,
      moduleCount: this.getByCategory(category).length,
      permissionCount: this.getByCategory(category)
        .reduce((sum, m) => sum + m.permissions.length, 0)
    }));

    return {
      totalModules: PERMISSION_MODULES.length,
      totalPermissions,
      categories: categoryStats
    };
  }
};